/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { asBase64, toStoredDataPart, retrieveAsBlob as genericRetrieveAsBlob, isLLMContentArray, isMetadataEntry, isLLMContent, isInlineData, isStoredData, } from "./common.js";
export class DefaultDataStore {
    #lastGroupId = null;
    #dataStores = new Map();
    #keyValueStore = new Map();
    createGroup(groupId) {
        let dataStore = this.#dataStores.get(groupId);
        if (dataStore) {
            throw new Error(`Group with ID already exists: ${groupId}`);
        }
        dataStore = new Map();
        this.#dataStores.set(groupId, dataStore);
        this.#lastGroupId = groupId;
    }
    async store(blob, groupId) {
        groupId ??= this.#lastGroupId ?? undefined;
        if (!groupId) {
            throw new Error("No active group - unable to store blob");
        }
        const dataStore = this.#dataStores.get(groupId);
        if (!dataStore) {
            throw new Error(`No group in data store with ID: ${groupId}`);
        }
        const part = await toStoredDataPart(blob);
        const nodeTimeStamp = Date.now().toFixed(3);
        let properties = dataStore.get(nodeTimeStamp);
        if (!properties) {
            properties = new Map();
            dataStore.set(nodeTimeStamp, properties);
        }
        properties.set(`direct-set-${nodeTimeStamp}`, new Map([[0, new Map([[0, part]])]]));
        return part;
    }
    async retrieveAsBlob(part, graphUrl) {
        return genericRetrieveAsBlob(part, graphUrl);
    }
    async replaceDataParts(groupId, result) {
        if (result.type !== "nodeend" || result.data.node.type !== "input") {
            return;
        }
        const dataStore = this.#dataStores.get(groupId);
        if (!dataStore) {
            throw new Error(`Unable to replace data parts, no group created for ${groupId}`);
        }
        const nodeTimeStamp = result.data.timestamp.toFixed(3);
        let properties = dataStore.get(nodeTimeStamp);
        if (!properties) {
            properties = new Map();
            dataStore.set(nodeTimeStamp, properties);
        }
        for (const [property, value] of Object.entries(result.data.outputs)) {
            if (!isLLMContent(value) && !isLLMContentArray(value)) {
                continue;
            }
            const values = isLLMContent(value) ? [value] : value;
            let propertyHandles = properties.get(property);
            if (!propertyHandles) {
                propertyHandles = new Map();
                properties.set(property, propertyHandles);
            }
            for (let i = 0; i < values.length; i++) {
                const value = values[i];
                if (isMetadataEntry(value)) {
                    continue;
                }
                for (let j = 0; j < value.parts.length; j++) {
                    const part = value.parts[j];
                    if (isInlineData(part)) {
                        let partHandles = propertyHandles.get(0);
                        if (!partHandles) {
                            partHandles = new Map();
                            propertyHandles.set(i, partHandles);
                        }
                        let storedDataPart = partHandles.get(j);
                        if (!storedDataPart) {
                            storedDataPart = await toStoredDataPart(part);
                        }
                        value.parts[j] = storedDataPart;
                        partHandles.set(j, storedDataPart);
                    }
                    else if (isStoredData(part)) {
                        let partHandles = propertyHandles.get(0);
                        if (!partHandles) {
                            partHandles = new Map();
                            propertyHandles.set(i, partHandles);
                        }
                        // This is a stored data from a previous run, so now we need to call
                        // toStoredDataPart again to create a new stored data part for this
                        // run.
                        const storedDataPart = await toStoredDataPart(part);
                        value.parts[j] = storedDataPart;
                        partHandles.set(j, storedDataPart);
                    }
                }
            }
        }
    }
    has(groupId) {
        return this.#dataStores.has(groupId);
    }
    async serializeGroup(groupId) {
        const nodes = this.#dataStores.get(groupId);
        if (!nodes) {
            return null;
        }
        let allHandles = [];
        for (const nodeTimeStamp of nodes.values()) {
            for (const outputProperty of nodeTimeStamp.values()) {
                for (const outputPropertyIndex of outputProperty.values()) {
                    const storedData = outputPropertyIndex.values();
                    allHandles = [...storedData].map(async (storedDataPart) => {
                        const { handle } = storedDataPart.storedData;
                        const response = await fetch(handle);
                        const blob = await response.blob();
                        const mimeType = blob.type;
                        const data = await asBase64(blob);
                        return { handle, inlineData: { mimeType, data } };
                    });
                }
            }
        }
        return Promise.all(allHandles);
    }
    releaseGroup(groupId) {
        const nodes = this.#dataStores.get(groupId);
        if (!nodes) {
            return;
        }
        for (const nodeTimeStamp of nodes.values()) {
            for (const outputProperty of nodeTimeStamp.values()) {
                for (const outputPropertyIndex of outputProperty.values()) {
                    for (const storedData of outputPropertyIndex.values()) {
                        URL.revokeObjectURL(storedData.storedData.handle);
                    }
                }
            }
        }
        this.#dataStores.delete(groupId);
        if (this.#lastGroupId === groupId) {
            this.#lastGroupId = null;
        }
    }
    releaseAll() {
        for (const groupId of this.#dataStores.keys()) {
            this.releaseGroup(groupId);
        }
        this.#lastGroupId = null;
    }
    async drop() {
        this.releaseAll();
        this.#dataStores.clear();
        return;
    }
    async storeData(key, value, schema) {
        // TODO: Implement scope handling.
        // Corresponds to the "session" scope.
        this.#keyValueStore.set(key, { value, schema });
        return { success: true };
    }
    async retrieveData(key) {
        const entry = this.#keyValueStore.get(key);
        if (!entry) {
            return { success: false, error: `No value found for key: ${key}` };
        }
        return { success: true, value: entry.value, schema: entry.schema };
    }
}
//# sourceMappingURL=default-data-store.js.map